import { useEffect, useState } from "react";
import { Bot, CheckCircle2, CirclePause, CirclePlay, Clock, Flag, FlaskConical, ListChecks, Play, RotateCcw, ShieldCheck, Zap } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, Checkbox, InlineAlert, cn, useSave } from "@ncp/ui";
import { useBuilder } from "../store";
import type { SimEffect, SimStep } from "../types";
import { TestRunPanel } from "./TestRunPanel";

export interface SimTrace { current: string | null; visited: string[]; edges: string[] }

const EFFECT_ICON: Record<string, typeof Play> = {
  clockStarted: Clock, clockCompleted: CheckCircle2, clockPaused: CirclePause, clockResumed: CirclePlay, taskCreated: ListChecks, tasksCancelled: ListChecks,
  agent: Bot, action: Zap, autoAdvance: Zap, ended: Flag, waiting: CirclePause, status: Play,
};

/** Test run: walk a pretend case through the graph on the canvas, answering guards yourself; nothing is created or sent. */
export function SimulatorPanel({ onTrace, real }: { onTrace: (t: SimTrace | null) => void; real?: { repoKey: string; branch: string; headCommitId: string; dirty: boolean } }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"dry" | "real">("dry");
  const api = useApi();
  const graph = useBuilder((s) => s.graph);
  const [steps, setSteps] = useState<SimStep[]>([]);
  const [guards, setGuards] = useState<Record<string, boolean>>({});
  const current = steps.at(-1);

  // A simulated step stores nothing: a failed request is reported with its cause under the generic heading, not as "Not saved".
  const run = useSave({
    mutationFn: (body: { state?: string; trigger?: string }) => api.post<SimStep>("/api/workflows/builder/simulate", { graph, state: body.state, trigger: body.trigger, openClocks: current?.openClocks ?? [], guards }),
    failed: t("admin:common.error"),
    onSuccess: (step) => { if (step.ok) setSteps((s) => [...s, step]); else setSteps((s) => [...s.slice(0, -1), { ...s.at(-1)!, error: step.error }]); },
  });

  useEffect(() => {
    if (!steps.length) { onTrace(null); return; }
    const visited = steps.flatMap((s) => [s.from, s.state]).filter((x): x is string => !!x);
    const edges = steps.filter((s) => s.from && s.trigger).map((s) => graph.edges.find((e) => e.from === s.from && e.trigger === s.trigger)?.id).filter((x): x is string => !!x);
    onTrace({ current: current?.state ?? null, visited, edges });
  }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onTrace(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-sand-200 p-3">
        <span className="grid size-8 place-items-center rounded-lg bg-camel-100 text-camel-800"><FlaskConical className="size-4" /></span>
        <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{t("admin:builder.sim.title")}</p><p className="text-[11px] text-muted-foreground">{t("admin:builder.sim.subtitle")}</p></div>
        {steps.length ? <Button size="sm" variant="ghost" onClick={() => { setSteps([]); setGuards({}); }}><RotateCcw /> {t("admin:builder.sim.reset")}</Button> : null}
      </div>
      {real ? (
        <div className="flex gap-0.5 border-b border-sand-200 p-1.5">
          {(["dry", "real"] as const).map((m) => (
            <button key={m} type="button" data-testid={`test-mode-${m}`} onClick={() => setMode(m)} className={cn("flex-1 rounded-lg px-2 py-1 text-[11px] font-medium", mode === m ? "bg-sand-900 text-white" : "text-sand-700 hover:bg-sand-100")}>{t(`admin:builder.testRun.mode.${m}`)}</button>
          ))}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 text-xs">
        {real && mode === "real" ? <TestRunPanel {...real} /> : !current ? (
          <div className="space-y-3 text-center">
            <p className="text-muted-foreground">{t("admin:builder.sim.intro")}</p>
            <Button variant="gold" loading={run.isPending} onClick={() => run.mutate({})}><Play /> {t("admin:builder.sim.start")}</Button>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-aegold-300 bg-aegold-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-aegold-800">{t("admin:builder.sim.now")}</p>
              <p className="text-sm font-semibold">{current.stateLabel}</p>
              <p className="text-[11px] text-muted-foreground">{t(`status.${current.status}`, { defaultValue: current.status })}{current.ended ? ` · ${t("admin:builder.sim.ended")}` : ""}</p>
            </div>
            {current.error ? <InlineAlert tone="warning" title={current.error} /> : null}
            {current.openClocks.length ? (
              <div className="flex flex-wrap gap-1">{current.openClocks.map((c) => <Badge key={c.key} size="sm" variant={c.paused ? "outline" : "gold"}><Clock /> {c.label} · {c.days}{c.workingDays ? "wd" : "d"}{c.paused ? " ⏸" : ""}</Badge>)}</div>
            ) : null}
            {!current.ended ? (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-sand-500">{t("admin:builder.sim.next")}</p>
                {current.available.map((a) => (
                  <div key={a.trigger} className="rounded-lg border border-sand-200 bg-white p-2">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="min-w-0 flex-1 justify-start overflow-hidden" loading={run.isPending} onClick={() => run.mutate({ state: current.state, trigger: a.trigger })}>
                        {a.automatic ? <Zap className="text-seablue-600" /> : <Play />}<span className="min-w-0 truncate">{a.label}</span><span className="ms-auto min-w-0 max-w-[45%] truncate text-[10px] text-muted-foreground">→ {a.toLabel}</span>
                      </Button>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted-foreground">
                      {a.roles.length ? <span>{a.roles.map((r) => t(`roles.${r}`, { defaultValue: r })).join(", ")}</span> : null}
                      {a.outcome ? <Badge size="sm" variant={a.outcome === "rejected" ? "danger" : "success"}>{a.outcome}</Badge> : null}
                      {a.hasCondition ? <Badge size="sm" variant="outline">{t("admin:builder.sim.condition")}</Badge> : null}
                    </div>
                    {a.guard ? (
                      <label className="mt-1.5 flex items-start gap-1.5 text-[10.5px]"><Checkbox checked={guards[a.guard] !== false} onCheckedChange={(v) => setGuards((g) => ({ ...g, [a.guard!]: !!v }))} className="mt-0.5" /><span><ShieldCheck className="me-1 inline size-3 text-aegold-700" />{a.guardDescription ?? a.guard}</span></label>
                    ) : null}
                  </div>
                ))}
                {current.available.length === 0 ? <p className="text-danger-700">{t("admin:builder.sim.deadEnd")}</p> : null}
              </div>
            ) : null}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-sand-500">{t("admin:builder.sim.log")}</p>
              <ol className="space-y-2 border-s border-sand-200 ps-3">
                {[...steps].reverse().map((s, i) => (
                  <li key={steps.length - i} className="space-y-1">
                    <p className="font-medium">{s.trigger ? `${s.trigger} → ${s.stateLabel}` : t("admin:builder.sim.opened", { state: s.stateLabel })}</p>
                    {s.effects.map((e, k) => <Effect key={k} effect={e} />)}
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Effect({ effect }: { effect: SimEffect }) {
  const Icon = EFFECT_ICON[effect.kind] ?? Play;
  return <p className={cn("flex items-start gap-1.5 text-[11px]", effect.kind === "agent" ? "text-seablue-800" : effect.kind === "action" || effect.kind === "autoAdvance" ? "text-seablue-700" : effect.kind.startsWith("clock") ? "text-aegold-800" : "text-muted-foreground")}><Icon className="mt-0.5 size-3 shrink-0" />{effect.text}</p>;
}
